/**
 * KinsCircle Phase 6B — AI Test Sandbox + Rollout Settings
 *
 * Owner / admin only. Mounted at /admin/ai. Strictly additive.
 *
 * Provides three things in one page:
 *   1. Rollout mode selector (disabled / owner_only / admin_only / paid_beta /
 *      all_premium). Persists locally per STABILITY.md §12.1.
 *   2. Per-feature flag toggles (memoryWriter, timeline, onThisDay, etc.).
 *      Local-only — to enable for all users, set the VITE_ENABLE_AI_* env var.
 *   3. AI Test Sandbox: invoke story / title / tags / tone / timeline / OTD
 *      against FAKE/SAMPLE data ONLY. Never reads real memories. Never writes.
 *      Output runs through the same safety filter as the live modal.
 *
 * NEVER touches family_graph_members, family_graph_memories, board_posts,
 * profiles, invites, or any storage object. NEVER exposes API keys.
 */

import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  Sparkles,
  ShieldCheck,
  Loader2,
  TestTube2,
  AlertTriangle,
} from 'lucide-react';

import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from '@/components/ui/card';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

import { useAuth } from '@/contexts/AuthContext';
import { Logo } from '@/components/Header';
import AIDiagnosticsCard from '@/components/ai/AIDiagnosticsCard';
import {
  isAIMemoryWriterEnabled,
  isAITimelineEnabled,
  isAIOnThisDayEnabled,
  isAIMemoryTagsEnabled,
  isAIRolloutControlEnabled,
  isAIUsageLimitsEnabled,
  isAIQualityFeedbackEnabled,
  isAISafetyFilterEnabled,
  isAITestSandboxEnabled,
  setAIFlagLocal,
  getAIRolloutMode,
  setAIRolloutMode,
  type AIRolloutMode,
  type FlagKey,
} from '@/lib/ai/featureFlag';
import {
  generateMemoryStory,
  generateMemoryTitle,
  generateMemoryTags,
  classifyMemoryTone,
  generateTimelineSummary,
  generateOnThisDaySummary,
} from '@/lib/ai/aiMemoryService';
import { validateAIStory, validateAIText } from '@/lib/ai/aiSafetyFilter';
import type { MemoryDraftInput } from '@/lib/ai/promptTemplates';

// Static, always-fake sample inputs. Never reads real family data.
const SAMPLE_MEMORY: MemoryDraftInput = {
  caption: 'Beach day with the kids — first time Lily put her toes in the ocean.',
  date: '2024-07-04',
  location: 'Half Moon Bay, CA',
  taggedNames: ['Lily', 'Marcus'],
  authorName: 'Sample User',
};
const SAMPLE_TIMELINE = {
  entries: [
    { year: 2018, summary: 'Family moved to a new house with a big backyard.' },
    { year: 2020, summary: 'Lily was born in the spring.' },
    { year: 2024, summary: 'First family trip to the beach.' },
  ],
};
const SAMPLE_OTD = {
  monthDay: '07-04',
  matches: [
    { year: 2022, caption: 'Backyard cookout, sparklers, watermelon.' },
    { year: 2024, caption: 'Beach day at Half Moon Bay.' },
  ],
};

type FlagRow = { key: FlagKey; label: string; on: boolean; desc: string };

const AITestSandbox: React.FC = () => {
  const navigate = useNavigate();
  const { isOwner, isAdmin, isAuthenticated, loading } = useAuth();

  const [rollout, setRollout] = useState<AIRolloutMode>(() => getAIRolloutMode());
  const [flagTick, setFlagTick] = useState(0);
  const [busy, setBusy] = useState<string | null>(null);
  const [output, setOutput] = useState<string>('');
  const [outputAction, setOutputAction] = useState<string>('');
  const [safetyNote, setSafetyNote] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const flags: FlagRow[] = useMemo(() => {
    // re-read every render via flagTick
    void flagTick;
    return [
      {
        key: 'memoryWriter',
        label: 'AI Memory Writer',
        on: isAIMemoryWriterEnabled(),
        desc: 'Story / title / tone draft generation in the memory composer.',
      },
      {
        key: 'timelineSummary',
        label: 'Timeline Summary',
        on: isAITimelineEnabled(),
        desc: 'AI-written narrative summary of a year-by-year timeline.',
      },
      {
        key: 'onThisDay',
        label: 'On This Day',
        on: isAIOnThisDayEnabled(),
        desc: 'AI recap card that surfaces same-month-day memories from past years.',
      },
      {
        key: 'memoryTags',
        label: 'Memory Tags',
        on: isAIMemoryTagsEnabled(),
        desc: 'AI-suggested short keyword tags for a memory.',
      },
      {
        key: 'rolloutControl',
        label: 'Rollout Control',
        on: isAIRolloutControlEnabled(),
        desc: 'Master gate that respects the rollout mode below.',
      },
      {
        key: 'usageLimits',
        label: 'Usage Limits',
        on: isAIUsageLimitsEnabled(),
        desc: 'Per-plan daily / weekly / monthly quotas (advisory).',
      },
      {
        key: 'qualityFeedback',
        label: 'Quality Feedback',
        on: isAIQualityFeedbackEnabled(),
        desc: 'Six-tag feedback chips after each AI draft.',
      },
      {
        key: 'safetyFilter',
        label: 'Safety Filter',
        on: isAISafetyFilterEnabled(),
        desc: 'Client-side rejection of fabricated facts, unknown names, and unsafe content.',
      },
      {
        key: 'testSandbox',
        label: 'Test Sandbox',
        on: isAITestSandboxEnabled(),
        desc: 'Allows AI invocations from this page using SAMPLE data only.',
      },
      {
        key: 'diagnostics',
        label: 'Diagnostics Card',
        on: false,
        desc: 'Owner/admin diagnostics surface (this card below).',
      },
    ];
  }, [flagTick]);

  const onToggleFlag = (k: FlagKey, next: boolean) => {
    setAIFlagLocal(k, next);
    setFlagTick((t) => t + 1);
  };

  const onChangeRollout = (mode: AIRolloutMode) => {
    setRollout(mode);
    setAIRolloutMode(mode);
  };

  const runTest = async (
    action:
      | 'story'
      | 'title'
      | 'tags'
      | 'tone'
      | 'timeline'
      | 'on_this_day',
  ) => {
    if (!isAITestSandboxEnabled()) {
      setError('Enable the "Test Sandbox" flag below to run AI tests.');
      return;
    }
    setBusy(action);
    setError(null);
    setSafetyNote(null);
    setOutput('');
    setOutputAction(action);
    try {
      let text = '';
      if (action === 'story') {
        const r = await generateMemoryStory(SAMPLE_MEMORY);
        if (!r.ok) {
          setError(`${r.code}: ${r.message}`);
          return;
        }
        text = r.data.story;
        const v = validateAIStory(text, SAMPLE_MEMORY);
        if (!v.safe) setSafetyNote(`Safety filter would reject this output: ${v.reasons.join(', ')}`);
      } else if (action === 'title') {
        const r = await generateMemoryTitle(SAMPLE_MEMORY);
        if (!r.ok) {
          setError(`${r.code}: ${r.message}`);
          return;
        }
        text = r.data.title;
      } else if (action === 'tags') {
        const r = await generateMemoryTags(SAMPLE_MEMORY);
        if (!r.ok) {
          setError(`${r.code}: ${r.message}`);
          return;
        }
        text = r.data.tags.join(', ');
      } else if (action === 'tone') {
        const r = await classifyMemoryTone(SAMPLE_MEMORY);
        if (!r.ok) {
          setError(`${r.code}: ${r.message}`);
          return;
        }
        text = r.data.tone;
      } else if (action === 'timeline') {
        const r = await generateTimelineSummary(SAMPLE_TIMELINE);
        if (!r.ok) {
          setError(`${r.code}: ${r.message}`);
          return;
        }
        text = r.data.summary;
        const v = validateAIText(text);
        if (!v.safe) setSafetyNote(`Safety filter would reject this output: ${v.reasons.join(', ')}`);
      } else if (action === 'on_this_day') {
        const r = await generateOnThisDaySummary(SAMPLE_OTD);
        if (!r.ok) {
          setError(`${r.code}: ${r.message}`);
          return;
        }
        text = r.data.recap;
        const v = validateAIText(text);
        if (!v.safe) setSafetyNote(`Safety filter would reject this output: ${v.reasons.join(', ')}`);
      }
      setOutput(text);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unknown error');
    } finally {
      setBusy(null);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <Loader2 className="w-8 h-8 animate-spin text-fuchsia-600" />
      </div>
    );
  }

  if (!isAuthenticated || !(isOwner || isAdmin)) {
    return (
      <div className="min-h-screen bg-slate-50 flex flex-col items-center justify-center p-6 text-center">
        <ShieldCheck className="w-10 h-10 text-slate-400 mb-3" />
        <h1 className="text-2xl font-bold text-slate-900 mb-2">Admins only</h1>
        <p className="text-slate-600 mb-6 max-w-md">
          The KinsCircle AI Settings + Test Sandbox is restricted to family
          owners and admins.
        </p>
        <Button onClick={() => navigate('/')} className="rounded-full">
          Back to home
        </Button>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="sticky top-0 z-30 bg-white/95 backdrop-blur-md border-b border-slate-100 shadow-sm">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 h-20 flex items-center justify-between">
          <button onClick={() => navigate('/')} aria-label="KinsCircle home">
            <Logo />
          </button>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              onClick={() => navigate('/admin/health')}
              className="rounded-full gap-1.5"
            >
              <ArrowLeft className="w-4 h-4" />
              System Health
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-10 space-y-8">
        <div>
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-fuchsia-50 text-fuchsia-700 border border-fuchsia-200 text-xs font-semibold mb-2">
            <ShieldCheck className="w-3.5 h-3.5" />
            Admin only · Local browser only
          </div>
          <h1 className="text-3xl font-bold text-slate-900 flex items-center gap-2">
            <Sparkles className="w-8 h-8 text-fuchsia-600" />
            AI Settings + Test Sandbox
          </h1>
          <p className="text-slate-600 mt-1 max-w-2xl">
            Configure rollout, toggle individual AI features for THIS browser,
            and run end-to-end AI tests against sample data. No real family
            memories are read or written from this page.
          </p>
        </div>

        {/* Critical persistence warning */}
        <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-amber-900 text-xs flex items-start gap-2">
          <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />
          <div>
            <span className="font-semibold">These toggles persist only in this browser.</span>{' '}
            To enable AI for all users, set the corresponding{' '}
            <code className="px-1 rounded bg-white border border-amber-200">VITE_ENABLE_AI_*</code>{' '}
            env var at build time.
          </div>
        </div>

        {/* Rollout mode */}
        <Card>
          <CardHeader>
            <CardTitle>Rollout mode</CardTitle>
            <CardDescription>
              Active only when the <span className="font-mono">rolloutControl</span> flag is on.
              Controls who can see / use AI features in this app.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            <Select value={rollout} onValueChange={(v) => onChangeRollout(v as AIRolloutMode)}>
              <SelectTrigger className="w-full sm:w-80">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="disabled">Disabled — nobody (default)</SelectItem>
                <SelectItem value="owner_only">Owner only — test mode</SelectItem>
                <SelectItem value="admin_only">Admin only — staff test</SelectItem>
                <SelectItem value="paid_beta">Paid users beta</SelectItem>
                <SelectItem value="all_premium">All premium users</SelectItem>
              </SelectContent>
            </Select>
            <div className="text-xs text-slate-600">
              Current mode: <span className="font-semibold">{rollout}</span>.
              {!isAIRolloutControlEnabled() && (
                <span className="ml-1 text-amber-700">
                  (rolloutControl flag is OFF — legacy gating is in effect)
                </span>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Per-feature flags */}
        <Card>
          <CardHeader>
            <CardTitle>Feature flags</CardTitle>
            <CardDescription>
              Each toggle flips the local-storage override for this browser only.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid sm:grid-cols-2 gap-3">
              {flags.map((f) => (
                <div
                  key={f.key}
                  className="rounded-lg border border-slate-200 bg-white p-3 flex items-start justify-between gap-3"
                >
                  <div className="flex-1 min-w-0">
                    <Label className="text-sm font-semibold text-slate-800">{f.label}</Label>
                    <p className="text-[11px] text-slate-500 mt-0.5">{f.desc}</p>
                  </div>
                  <Switch
                    checked={f.on}
                    onCheckedChange={(next) => onToggleFlag(f.key, next)}
                  />
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Sandbox runner */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <TestTube2 className="w-5 h-5 text-fuchsia-600" />
              Sandbox runner
            </CardTitle>
            <CardDescription>
              Invokes the ai-memory-assistant edge function with hard-coded
              sample input. Real family data is never read.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="rounded-lg bg-slate-50 border border-slate-200 p-3 text-xs text-slate-700">
              <div className="font-semibold mb-1">Sample memory input (read-only)</div>
              <div>caption: <span className="font-mono">{SAMPLE_MEMORY.caption}</span></div>
              <div>date: <span className="font-mono">{SAMPLE_MEMORY.date}</span></div>
              <div>location: <span className="font-mono">{SAMPLE_MEMORY.location}</span></div>
              <div>tagged: <span className="font-mono">{(SAMPLE_MEMORY.taggedNames ?? []).join(', ')}</span></div>
            </div>
            <div className="flex flex-wrap gap-2">
              {[
                { id: 'story', label: 'Story' },
                { id: 'title', label: 'Title' },
                { id: 'tags', label: 'Tags' },
                { id: 'tone', label: 'Tone' },
                { id: 'timeline', label: 'Timeline' },
                { id: 'on_this_day', label: 'On This Day' },
              ].map((b) => (
                <Button
                  key={b.id}
                  variant="outline"
                  size="sm"
                  className="rounded-full gap-1.5"
                  disabled={busy !== null}
                  onClick={() => void runTest(b.id as any)}
                >
                  {busy === b.id ? (
                    <Loader2 className="w-3.5 h-3.5 animate-spin" />
                  ) : (
                    <Sparkles className="w-3.5 h-3.5" />
                  )}
                  {b.label}
                </Button>
              ))}
            </div>
            {error && (
              <div className="rounded-md border border-rose-200 bg-rose-50 p-2.5 text-xs text-rose-800 flex items-start gap-1.5">
                <AlertTriangle className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" />
                <span>{error}</span>
              </div>
            )}
            {safetyNote && (
              <div className="rounded-md border border-amber-200 bg-amber-50 p-2.5 text-xs text-amber-900">
                {safetyNote}
              </div>
            )}
            {output && (
              <div>
                <div className="text-[11px] font-semibold text-slate-600 uppercase tracking-wide mb-1">
                  Output ({outputAction})
                </div>
                <Textarea value={output} readOnly rows={6} className="resize-none font-mono text-xs" />
              </div>
            )}
          </CardContent>
        </Card>

        {/* Live diagnostics */}
        <AIDiagnosticsCard />
      </main>
    </div>
  );
};

export default AITestSandbox;
